import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, normalizeStyle as _normalizeStyle, resolveDirective as _resolveDirective, withDirectives as _withDirectives, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "flex column full-height" }
const _hoisted_2 = {
  key: 0,
  class: "column flex-center full-height text-quote-text"
}
const _hoisted_3 = { class: "text-h3 q-my-xs" }
const _hoisted_4 = {
  key: 0,
  class: "text-body1 text-italic q-mt-md"
}
const _hoisted_5 = { class: "row full-height flex flex-center" }
const _hoisted_6 = { class: "col image-overview-previous" }
const _hoisted_7 = { class: "image-overview-bad-image column absolute-full flex-center transparent" }
const _hoisted_8 = { class: "text-error" }
const _hoisted_9 = { class: "col" }
const _hoisted_10 = { class: "column absolute-full flex-center transparent" }
const _hoisted_11 = { class: "text-error" }
const _hoisted_12 = { class: "col image-overview-next" }
const _hoisted_13 = { class: "image-overview-bad-image column absolute-full flex-center transparent" }
const _hoisted_14 = { class: "text-error" }
const _hoisted_15 = { class: "text-h4 text-navy-base" }
const _hoisted_16 = { class: "text-h5 text-fun-label-and-icon" }
const _hoisted_17 = { class: "absolute-full column flex-center transparent image-gallery-item--error" }
const _hoisted_18 = { class: "text-error" }

import axios from "axios"
import { date, QFile } from "quasar"
import SfxBtnOutline from "src/components/button/SfxBtnOutline.vue"
import { getDB } from "src/db/databases"
import { useConfigurationStore } from "src/stores/configurationStore"
import { Log } from "src/util/classes/log"
import { DATE_TIME_FULL_MASK } from "src/util/constants/configurations"
import {
  ATTACHMENT_IMAGE_EXTENSIONS,
  IMAGE_FILE_MAIN_MIME_TYPES,
  MAX_UPLOAD_FILE_SIZE,
} from "src/util/constants/files"
import { Icons } from "src/util/enums/fontAwesomeIcons"
import {
  deleteEntityAttachments,
  getAttachmentCardData,
  getEntityAttachmentIdList,
  partitionBatchErrors,
  removeBatchErrorsAndPrint,
  updateEntityInUI,
} from "src/util/functions/attachments"
import { imageDeleteDialog } from "src/util/functions/dialogs"
import { getTranslatedEntityForAttachment } from "src/util/functions/entity"
import { postAttachmentForEntityBatch } from "src/util/functions/entityData"
import {
  showAttachmentUploadingNotification,
  showErrorNotification,
  showFileTypeNotAllowedPartialNotification,
  showIssueDownloadingFileNotification,
  showMaxFileSizeExceededNotification,
} from "src/util/functions/notifications"
import { ImageViewerItem } from "src/util/types/components/sfxImageGallery"
import { AttachmentContext, AttachmentUpload } from "src/util/types/dataModels/attachment"
import { DragAndDropValue } from "src/util/types/misc"
import { computed, PropType, ref, shallowRef, watch } from "vue"
import { useI18n } from "vue-i18n"

//region Props and Emits

const canUpload = true
const ICON_SIZE = "48px"
const THUMBNAIL_SIZE = "56px"


export default /*#__PURE__*/_defineComponent({
  __name: 'SfxImageViewerManager',
  props: {
  entityId: {
    type: String,
    required: true,
  },

  entityType: {
    type: String as PropType<AttachmentContext>,
    required: true,
  },

  imageList: {
    type: Array as PropType<ImageViewerItem[]>,
    required: true,
  },

  startImage: {
    type: Object as PropType<ImageViewerItem>,
  },

  disableEditImage: Boolean,
},
  emits: ["update:imageList"],
  setup(__props, { emit: __emit }) {

const props = __props

const emit = __emit

//endregion

//region Variables

const { t } = useI18n()

const translatedEntity = computed(() => getTranslatedEntityForAttachment(props.entityType))

const dndContent = t("sfxImage.dnd_content", { entity: translatedEntity.value })
const dndQuote = t("sfxImage.dnd_quote")
const configurationStore = useConfigurationStore()
const easterEggsEnabled = computed(() => configurationStore.easterEggs)

const slideModel = shallowRef<number>(
  props.startImage && props.imageList.includes(props.startImage)
    ? props.imageList.indexOf(props.startImage)
    : 0
)

const imageList = computed({
  get() {
    return props.imageList
  },

  set(value) {
    return emit("update:imageList", value)
  },
})

const currentImage = computed<ImageViewerItem | null>(
  () => props.imageList.at(slideModel.value) || null
)
const previousImage = computed<ImageViewerItem | null>(
  () => props.imageList.at(slideModel.value - 1) || null
)
const nextImage = computed<ImageViewerItem | null>(
  () =>
    props.imageList.at(
      props.imageList.length - 1 === slideModel.value ? 0 : slideModel.value + 1
    ) || null
)

const filePicker = ref<QFile | null>(null)
const filePickerFiles = ref<File[]>([])

const canDownload = computed(
  () => Boolean(currentImage.value) && currentImage.value?.attachmentData.state === 2
)
const canDelete = computed(
  () =>
    Boolean(currentImage.value) &&
    (currentImage.value?.attachmentData.state === 2 ||
      currentImage.value?.attachmentData.state === -1)
)

const thumbnails = shallowRef()

const imageDetailsDiv = ref()
const actionButtonsDiv = ref()
const thumbnailsDiv = ref()

const carouselStyle = computed(() => {
  // 48 is the actionButtonsDiv margin
  const actionButtonsDivHeight = actionButtonsDiv.value?.offsetHeight + 48

  if (imageList.value?.length > 0) {
    const imageDetailsDivHeight = imageDetailsDiv.value?.offsetHeight
    const thumbnailsDivHeight = thumbnailsDiv.value?.offsetHeight
    const totalHeight = imageDetailsDivHeight + actionButtonsDivHeight + thumbnailsDivHeight

    return "height: " + `calc(100% - ${totalHeight}px)`
  } else {
    // 72 is the height of the thumbnailsDiv when it has thumbnails
    // Hardcoded this value to avoid the action button moving when there are no thumbnails
    return "height: " + `calc(100% - ${actionButtonsDivHeight + 72}px)`
  }
})

const disableBtnAction = computed(() => props.entityType === "issue_item" || props.disableEditImage)

//endregion

//region Functions

function openImage(image: ImageViewerItem) {
  window.open(image.src, "_blank")
}

function dragScroll(offset: { x: number; y: number }) {
  const acceleration = 0.1 // control speed gain
  const scrollBy = (thumbnails.value.$el.offsetWidth / thumbnails.value.$el.scrollWidth) * -offset.x
  thumbnails.value?.$el.scrollBy({ left: scrollBy * acceleration })
}

function setImageIndex(index: number) {
  slideModel.value = index
}

function getFormattedDate(dateTime: string): string {
  return date.formatDate(dateTime, DATE_TIME_FULL_MASK)
}

async function downloadFile() {
  if (!currentImage.value) return

  try {
    const result = await axios.get(currentImage.value.src, { responseType: "blob" })

    const blob = new Blob([result.data])
    const link = document.createElement("a")
    link.href = URL.createObjectURL(blob)
    link.download =
      currentImage.value.attachmentData.name + "." + currentImage.value.attachmentData.extension
    link.click()
    URL.revokeObjectURL(link.href)
  } catch (error) {
    showIssueDownloadingFileNotification()
    Log.error({
      message: "Bad image download from image viewer",
      error,
      data: { image: currentImage.value },
    })
  }
}

function dropFile(dndValue: DragAndDropValue) {
  // User not allowed to upload
  if (disableBtnAction.value) return

  handleFileUpload(dndValue.detail)
}

function handleFileUpload(files: File[]) {
  const allowedFiles: AttachmentUpload[] = []
  const disallowedFiles: File[] = []
  let filesSize = 0

  for (const file of files) {
    const extension = file.name.split(".").pop()
    if (extension && ATTACHMENT_IMAGE_EXTENSIONS.includes(extension)) {
      allowedFiles.push({ data: file })
      filesSize += file.size
    } else {
      disallowedFiles.push(file)
    }
  }

  if (disallowedFiles.length > 0) {
    showFileTypeNotAllowedPartialNotification(disallowedFiles)
  }

  if (filesSize > MAX_UPLOAD_FILE_SIZE) {
    showMaxFileSizeExceededNotification()
    return
  }

  saveNewImage(allowedFiles)
}

async function saveNewImage(newImages: AttachmentUpload[]) {
  const singleImage = newImages.length === 1
  const dismissNotify = showAttachmentUploadingNotification(singleImage)

  try {
    const newImagesInServer = await postAttachmentForEntityBatch(
      props.entityType,
      props.entityId,
      newImages
    )

    if (!newImagesInServer) return

    const successfulImages = removeBatchErrorsAndPrint(partitionBatchErrors(newImagesInServer))

    const attachmentIds = (await getEntityAttachmentIdList(props.entityType, props.entityId)) || []

    for (const newImage of successfulImages) {
      if (attachmentIds.includes(newImage.id)) {
        const existingImageIndex = imageList.value.findIndex(
          (image) => image.attachmentData.id === newImage.id
        )
        // If the image name is different, update the image new name in the UI
        if (newImage.name !== imageList.value[existingImageIndex].attachmentData.name) {
          imageList.value[existingImageIndex].attachmentData.name = newImage.name
        }
        continue
      }

      // Add the new image to the overlay UI
      const attachmentCardData: ImageViewerItem = await getAttachmentCardData(
        newImage,
        props.entityType,
        props.entityId
      )
      imageList.value.push(attachmentCardData)
    }

    // Since the entity has new attachment ids we need to update the entity in the UI as well
    await updateEntityInUI(props.entityType, props.entityId)

    const db = await getDB()
    // bulkUpsert is intentionally used here to handle the case where the user adding the same image again
    await db.attachment.bulkUpsert(successfulImages)

    dismissNotify()
  } catch (error) {
    dismissNotify()
    showErrorNotification(t("sfxErrorMsg.error_adding_image"))
    Log.error({ message: "An error occurred while adding the image", error })
  }
}

async function deleteAttachment() {
  if (!currentImage.value?.attachmentData) return

  const userConfirmed = await imageDeleteDialog(translatedEntity.value, currentImage.value)
  if (!userConfirmed) return

  const response = await deleteEntityAttachments(
    [currentImage.value?.attachmentData.id],
    props.entityType,
    props.entityId
  )

  // We remove the image from the UI if the DELETE API response is
  // 204 - The attachment has been removed successfully
  // OR
  // 404 - Cannot find the attachment(the attachment is already removed)
  if (response && (response[0].status === 204 || response[0].status === 404)) {
    // Update the UI
    const deletedImageIndex = imageList.value?.findIndex(
      (image) => image.attachmentData.id === currentImage.value?.attachmentData.id
    )
    imageList.value.splice(deletedImageIndex, 1)
  } else {
    showErrorNotification(
      t("sfxErrorMsg.error_deleting_attachment_with_name", {
        fileName: currentImage.value?.attachmentData.name,
      })
    )
  }
}

//endregion

//region Setup Code

watch(
  () => filePickerFiles.value,
  (newFiles) => {
    if (newFiles) {
      handleFileUpload(newFiles)
      // Reset model so that same file can be uploaded again, so it is consistent with DND
      newFiles.length = 0
    }
  }
)

//endregion

return (_ctx: any,_cache: any) => {
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_img = _resolveComponent("q-img")!
  const _component_q_carousel_slide = _resolveComponent("q-carousel-slide")!
  const _component_q_carousel_control = _resolveComponent("q-carousel-control")!
  const _component_q_carousel = _resolveComponent("q-carousel")!
  const _component_q_list = _resolveComponent("q-list")!
  const _directive_dnd = _resolveDirective("dnd")!
  const _directive_touch_pan = _resolveDirective("touch-pan")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _withDirectives((_openBlock(), _createElementBlock("div", {
      style: _normalizeStyle(carouselStyle.value),
      onDnd: dropFile
    }, [
      (imageList.value?.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(dndContent)), 1),
            (easterEggsEnabled.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(_unref(dndQuote)), 1))
              : _createCommentVNode("", true)
          ]))
        : (_openBlock(), _createBlock(_component_q_carousel, {
            key: 1,
            ref: "carousel",
            modelValue: slideModel.value,
            "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((slideModel).value = $event)),
            class: "transparent",
            swipeable: "",
            animated: "",
            infinite: "",
            height: "100%",
            "transition-prev": "slide-right",
            "transition-next": "slide-left"
          }, {
            control: _withCtx(() => [
              _createVNode(_component_q_carousel_control, {
                position: "left",
                class: "column justify-center"
              }, {
                default: _withCtx(() => [
                  _createVNode(SfxBtnOutline, {
                    class: "bg-white",
                    icon: _unref(Icons).CHEVRON_LEFT,
                    onClick: _cache[2] || (_cache[2] = ($event: any) => (_ctx.$refs.carousel?.previous()))
                  }, null, 8, ["icon"])
                ]),
                _: 1
              }),
              _createVNode(_component_q_carousel_control, {
                position: "right",
                class: "column justify-center"
              }, {
                default: _withCtx(() => [
                  _createVNode(SfxBtnOutline, {
                    class: "bg-white",
                    icon: _unref(Icons).CHEVRON_RIGHT,
                    onClick: _cache[3] || (_cache[3] = ($event: any) => (_ctx.$refs.carousel?.next()))
                  }, null, 8, ["icon"])
                ]),
                _: 1
              })
            ]),
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.imageList, (image, index) => {
                return (_openBlock(), _createBlock(_component_q_carousel_slide, {
                  key: index,
                  name: index
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                      _createElementVNode("div", _hoisted_6, [
                        _createVNode(_component_q_img, {
                          src: previousImage.value?.src,
                          class: "image-overview-previous-image",
                          ratio: "1",
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$refs.carousel?.previous()))
                        }, {
                          error: _withCtx(() => [
                            _createElementVNode("div", _hoisted_7, [
                              _createVNode(_component_q_icon, {
                                name: _unref(Icons).IMAGE_ERROR,
                                class: "text-disabled",
                                size: ICON_SIZE
                              }, null, 8, ["name"]),
                              _createElementVNode("i", _hoisted_8, _toDisplayString(_ctx.$t("sfxImage.no_image")), 1)
                            ])
                          ]),
                          _: 1
                        }, 8, ["src"])
                      ]),
                      _createElementVNode("div", _hoisted_9, [
                        _createVNode(_component_q_img, {
                          src: image.src,
                          class: "fit",
                          onClick: ($event: any) => (openImage(image)),
                          ratio: "1"
                        }, {
                          error: _withCtx(() => [
                            _createElementVNode("div", _hoisted_10, [
                              _createVNode(_component_q_icon, {
                                name: _unref(Icons).IMAGE_ERROR,
                                class: "text-disabled",
                                size: ICON_SIZE
                              }, null, 8, ["name"]),
                              _createElementVNode("i", _hoisted_11, _toDisplayString(_ctx.$t("sfxImage.no_image")), 1)
                            ])
                          ]),
                          _: 2
                        }, 1032, ["src", "onClick"])
                      ]),
                      _createElementVNode("div", _hoisted_12, [
                        _createVNode(_component_q_img, {
                          src: nextImage.value?.src,
                          class: "image-overview-next-image",
                          ratio: "1",
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$refs.carousel?.next()))
                        }, {
                          error: _withCtx(() => [
                            _createElementVNode("div", _hoisted_13, [
                              _createVNode(_component_q_icon, {
                                name: _unref(Icons).IMAGE_ERROR,
                                class: "text-disabled",
                                size: ICON_SIZE
                              }, null, 8, ["name"]),
                              _createElementVNode("i", _hoisted_14, _toDisplayString(_ctx.$t("sfxImage.no_image")), 1)
                            ])
                          ]),
                          _: 1
                        }, 8, ["src"])
                      ])
                    ])
                  ]),
                  _: 2
                }, 1032, ["name"]))
              }), 128))
            ]),
            _: 1
          }, 8, ["modelValue"]))
    ], 36)), [
      [_directive_dnd]
    ]),
    (currentImage.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          ref_key: "imageDetailsDiv",
          ref: imageDetailsDiv,
          class: "column items-center"
        }, [
          _createElementVNode("span", _hoisted_15, _toDisplayString(currentImage.value.attachmentData.name), 1),
          _createElementVNode("span", _hoisted_16, _toDisplayString(getFormattedDate(currentImage.value.attachmentData.created_dt)), 1)
        ], 512))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      ref_key: "actionButtonsDiv",
      ref: actionButtonsDiv,
      class: "row justify-center q-gutter-md q-my-lg"
    }, [
      _createVNode(SfxBtnOutline, {
        icon: _unref(Icons).UPLOAD,
        disable: !canUpload || disableBtnAction.value,
        onClick: _cache[6] || (_cache[6] = ($event: any) => (_ctx.$refs.filePicker?.pickFiles()))
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(QFile), {
            class: "hidden",
            ref_key: "filePicker",
            ref: filePicker,
            modelValue: filePickerFiles.value,
            "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((filePickerFiles).value = $event)),
            accept: _unref(IMAGE_FILE_MAIN_MIME_TYPES),
            multiple: ""
          }, null, 8, ["modelValue", "accept"])
        ]),
        _: 1
      }, 8, ["icon", "disable"]),
      _createVNode(SfxBtnOutline, {
        icon: _unref(Icons).DOWNLOAD,
        disable: !canDownload.value,
        onClick: _cache[7] || (_cache[7] = ($event: any) => (downloadFile()))
      }, null, 8, ["icon", "disable"]),
      _createVNode(SfxBtnOutline, {
        icon: _unref(Icons).DELETE,
        disable: !canDelete.value || disableBtnAction.value,
        onClick: _cache[8] || (_cache[8] = ($event: any) => (deleteAttachment()))
      }, null, 8, ["icon", "disable"])
    ], 512),
    _createElementVNode("div", {
      ref_key: "thumbnailsDiv",
      ref: thumbnailsDiv,
      class: "full-width justify-center flex flex-center"
    }, [
      _withDirectives((_openBlock(), _createBlock(_component_q_list, {
        ref_key: "thumbnails",
        ref: thumbnails,
        class: "row no-wrap scroll image-gallery q-pb-md"
      }, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.imageList, (image, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              class: "image-gallery-item"
            }, [
              _createVNode(_component_q_img, {
                src: image.src,
                height: THUMBNAIL_SIZE,
                width: THUMBNAIL_SIZE,
                class: _normalizeClass(["image-gallery-item--image image-overview-thumbnail", { 'image-gallery-item--image-selected': index === slideModel.value }]),
                onClick: ($event: any) => (setImageIndex(index))
              }, {
                error: _withCtx(() => [
                  _createElementVNode("div", _hoisted_17, [
                    _createVNode(_component_q_icon, {
                      name: _unref(Icons).IMAGE_ERROR,
                      class: "text-disabled",
                      size: "25px"
                    }, null, 8, ["name"]),
                    _createElementVNode("i", _hoisted_18, _toDisplayString(_ctx.$t("sfxImage.no_image_small")), 1)
                  ])
                ]),
                _: 2
              }, 1032, ["src", "class", "onClick"])
            ]))
          }), 128))
        ]),
        _: 1
      })), [
        [
          _directive_touch_pan,
          ({ offset }) => dragScroll(offset),
          void 0,
          {
            mouse: true,
            horizontal: true
          }
        ]
      ])
    ], 512)
  ]))
}
}

})